import React, { useState, useEffect, useCallback } from 'react';
import ListLoadErrorBanner from '../../components/ui/ListLoadErrorBanner';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../../components/ui/Layout';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import ValidatedInput from '../../components/ui/ValidatedInput';
import ValidatedTextarea from '../../components/ui/ValidatedTextarea';
import Select from '../../components/ui/Select';
import ActionDropdown from '../../components/ui/ActionDropdown';
import ActionButtonsRow from '../../components/ui/ActionButtonsRow';
import SearchFilterCard, { FilterConfig } from '../../components/ui/SearchFilterCard';
import BulkActionsBar from '../../components/ui/BulkActionsBar';
import Pagination from '../../components/ui/Pagination';
import { useUserPageSize } from '../../hooks/useUserPageSize';
import { CanCreate, CanEdit, CanDelete } from '../../components/auth/PermissionGuard';
import { usePermission } from '../../hooks/usePermission';
import {
  RiEditLine,
  RiDeleteBinLine,
  RiEyeLine,
  RiSimCard2Line,
  RiDeleteBin2Line,
  RiFileDownloadLine,
  RiToggleLine,
  RiToggleFill
} from 'react-icons/ri';
import simPackageService, {
  SimPackage,
  SimPackageForm,
  Carrier,
  BillingCycle,
  CarrierLabels,
  BillingCycleLabels
} from '../../services/simPackageService';
import { showAlert, showConfirm } from '../../utils/alert';
import { mapFieldErrors, focusFirstInvalidField } from '../../utils/mapFieldErrors';
import { queryKeys } from '../../queryClient';
import { PAGINATION_CONFIG } from '../../config/pagination';
import { formatDateTimeFull } from '../../utils/dateUtils';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { useClampPage } from '../../hooks/useClampPage';
import { runBulkDelete, bulkDeleteFailureMessage } from '../../utils/bulkDelete';

const SimPackagePage: React.FC = () => {
  const queryClient = useQueryClient();
  const { canView, canCreate, canEdit, canDelete } = usePermission();

  // Permission checks for sim packages (ใช้ 'sims' module เหมือน SIM management)
  const MODULE = 'sims';
  const canViewPackage = canView(MODULE);
  const canCreatePackage = canCreate(MODULE);
  const canEditPackage = canEdit(MODULE);
  const canDeletePackage = canDelete(MODULE);

  // S1114 URL-sync: read URL params for lazy init (filter+page bidirectional)
  const [searchParams, setSearchParams] = useSearchParams();
  // BUG-S795 systemic (S796): กัน rapid double-click ยิง create mutate ซ้ำ (edit ใช้ ifMatch optimistic-lock 409 อยู่แล้ว)
  const isSubmittingRef = React.useRef(false);
  const urlPageParam = searchParams.get('page');
  const urlCarrierParam = searchParams.get('carrier') || '';
  const urlBillingParam = searchParams.get('billing') || '';
  const urlStatusParam = searchParams.get('status') || '';

  // State for search and pagination
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(() => {
    if (!urlPageParam) return 1;
    const n = parseInt(urlPageParam, 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, 9999);
  });
  const [pageSize, setPageSize] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  useUserPageSize(pageSize, setPageSize);

  // State for filters
  // S1114 URL-sync: hydrate filters from URL (length-bounded for safety, backend will reject invalid enum)
  const [filterCarrier, setFilterCarrier] = useState<string>(
    urlCarrierParam.length > 0 && urlCarrierParam.length <= 30 ? urlCarrierParam : ''
  );
  const [filterBillingCycle, setFilterBillingCycle] = useState<string>(
    urlBillingParam.length > 0 && urlBillingParam.length <= 30 ? urlBillingParam : ''
  );
  const [filterStatus, setFilterStatus] = useState<string>(
    urlStatusParam === 'active' || urlStatusParam === 'inactive' ? urlStatusParam : ''
  );

  // S1114 URL-sync: write filter+page to URL (Variant A overwrite — multi-field with inline page reset)
  const syncFiltersToUrl = useCallback((carrier: string, billing: string, status: string, page: number) => {
    const newParams = new URLSearchParams();
    if (carrier) newParams.set('carrier', carrier);
    if (billing) newParams.set('billing', billing);
    if (status === 'active' || status === 'inactive') newParams.set('status', status);
    if (page > 1) newParams.set('page', String(page));
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    syncFiltersToUrl(filterCarrier, filterBillingCycle, filterStatus, currentPage);
  }, [filterCarrier, filterBillingCycle, filterStatus, currentPage, syncFiltersToUrl]);

  // State for Modal
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedPackage, setSelectedPackage] = useState<SimPackage | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');

  // State for Form
  const [formData, setFormData] = useState<SimPackageForm>({
    code: '',
    carrier: Carrier.TRUE,
    name: '',
    speed_mbps: undefined,
    data_quota_gb: undefined,
    billing_cycle: BillingCycle.MONTHLY,
    monthly_cost: undefined,
    yearly_cost: undefined,
    is_active: true,
  });

  // Validation state
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [selectedPackages, setSelectedPackages] = useState<number[]>([]);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);

  // Clear selection when filters or pagination change
  useEffect(() => {
    setSelectedPackages([]);
  }, [currentPage, pageSize, searchTerm, filterCarrier, filterBillingCycle, filterStatus]);

  // React Query for data fetching
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['simPackages', currentPage, pageSize, searchTerm, filterCarrier, filterBillingCycle, filterStatus],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        carrier: filterCarrier ? (filterCarrier as Carrier) : undefined,
        billing_cycle: filterBillingCycle ? (filterBillingCycle as BillingCycle) : undefined,
        is_active: filterStatus ? filterStatus === 'active' : undefined,
      };
      return await simPackageService.getSimPackages(params);
    },
  });

  const packages = data?.items || [];
  const totalItems = data?.total || 0;
  useClampPage(currentPage, data?.total, pageSize, setCurrentPage, isLoading);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: SimPackageForm) => simPackageService.createSimPackage(data),
    onSuccess: () => {
      showAlert('สำเร็จ', 'สร้างแพ็กเกจซิมเรียบร้อยแล้ว', 'success');
      closeModal();
      refetch();
    },
    onError: (error: any) => {
      // S1218 FIX (BUG-S1218-005): handle 422 field_errors inline FIRST · replaces brittle string matching
      const fieldErrs = mapFieldErrors(error);
      if (fieldErrs) {
        setFormErrors({ ...formErrors, ...fieldErrs });
        focusFirstInvalidField(fieldErrs);
        return;
      }

      // ตรวจสอบว่าเป็น error เกี่ยวกับรหัสซ้ำหรือไม่ (legacy fallback for non-422 BUSINESS_RULE_VIOLATION)
      const errorData = error.response?.data?.error || error.response?.data;
      const errorMessage = errorData?.message || errorData?.detail || '';
      const errorCode = errorData?.code;
      const errorField = errorData?.field;

      // ถ้าเป็น error เกี่ยวกับรหัสซ้ำ แสดง inline error
      if (
        errorMessage.includes('รหัสแพ็กเกจ') && errorMessage.includes('มีอยู่') ||
        errorMessage.includes('already exists') ||
        (errorCode === 'BUSINESS_RULE_VIOLATION' && errorMessage.includes('รหัส')) ||
        errorField === 'code'
      ) {
        setFormErrors({
          ...formErrors,
          code: 'รหัสแพ็กเกจนี้มีอยู่ในระบบแล้ว'
        });
        return; // ไม่แสดง toast
      }

      // กรณีอื่นๆ แสดง toast error
      showAlert('ผิดพลาด', errorMessage || 'ไม่สามารถสร้างแพ็กเกจซิมได้', 'error');
    },
  });

  const updateMutation = useMutation({
    // BUG-S162-P1: forward last-seen updated_at as ifMatch → backend optimistic lock (stale→409)
    mutationFn: ({ id, data, ifMatch }: { id: number; data: Partial<SimPackageForm>; ifMatch?: string }) =>
      simPackageService.updateSimPackage(id, data, ifMatch),
    onSuccess: () => {
      showAlert('สำเร็จ', 'อัปเดตแพ็กเกจซิมเรียบร้อยแล้ว', 'success');
      closeModal();
      refetch();
    },
    onError: (error: any) => {
      // BUG-S162-P1: optimistic-lock conflict — another tab/user saved first. Tell user to reload.
      // P-S601-1 FIX (PO 2026-08-20): refetch list ให้ token ใหม่ทันที ไม่ต้อง F5
      if (error.response?.status === 409) {
        showAlert('ข้อมูลถูกแก้ไขโดยผู้ใช้อื่น', 'ระบบโหลดข้อมูลล่าสุดให้แล้ว กรุณาปิดหน้าต่างนี้แล้วเปิดแก้ไขใหม่อีกครั้ง', 'error');
        refetch();
        return;
      }
      // S1218 FIX (BUG-S1218-005): handle 422 field_errors inline FIRST (Update flow)
      const fieldErrs = mapFieldErrors(error);
      if (fieldErrs) {
        setFormErrors({ ...formErrors, ...fieldErrs });
        focusFirstInvalidField(fieldErrs);
        return;
      }

      const errorData = error.response?.data?.error || error.response?.data;
      const errorMessage = errorData?.message || errorData?.detail || '';
      const errorCode = errorData?.code;
      const errorField = errorData?.field;

      if (
        errorMessage.includes('รหัสแพ็กเกจ') && errorMessage.includes('มีอยู่') ||
        errorMessage.includes('already exists') ||
        (errorCode === 'BUSINESS_RULE_VIOLATION' && errorMessage.includes('รหัส')) ||
        errorField === 'code'
      ) {
        setFormErrors({
          ...formErrors,
          code: 'รหัสแพ็กเกจนี้มีอยู่ในระบบแล้ว'
        });
        return;
      }

      showAlert('ผิดพลาด', errorMessage || 'ไม่สามารถอัปเดตแพ็กเกจซิมได้', 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => simPackageService.deleteSimPackage(id),
    onSuccess: () => {
      showAlert('สำเร็จ', 'ลบแพ็กเกจซิมเรียบร้อยแล้ว', 'success');
      refetch();
    },
    onError: (error: any) => {
      showAlert('ผิดพลาด', getApiErrorMessage(error, 'ไม่สามารถลบแพ็กเกจซิมได้'), 'error');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (id: number) => simPackageService.toggleSimPackageStatus(id),
    onSuccess: () => {
      showAlert('สำเร็จ', 'เปลี่ยนสถานะแพ็กเกจเรียบร้อยแล้ว', 'success');
      refetch();
    },
    onError: (error: any) => {
      showAlert('ผิดพลาด', getApiErrorMessage(error, 'ไม่สามารถเปลี่ยนสถานะได้'), 'error');
    },
  });

  // Handlers
  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedPackages(packages.map((pkg) => pkg.id));
    } else {
      setSelectedPackages([]);
    }
  };

  const handleSelectPackage = (packageId: number, selected: boolean) => {
    if (selected) {
      setSelectedPackages((prev) => [...prev, packageId]);
    } else {
      setSelectedPackages((prev) => prev.filter((id) => id !== packageId));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPackages.length === 0) return;

    const confirmed = await showConfirm(
      'ยืนยันการลบ',
      `คุณต้องการลบแพ็กเกจซิมที่เลือก ${selectedPackages.length} รายการ ใช่หรือไม่?`,
      undefined,
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );

    if (confirmed) {
      setBulkLoading(true);
      try {
        // KD3 S2120 (BUG-S2120-003): เดิมวน await แล้วหยุดที่รายการแรกที่ล้ม — ไม่บอกว่ารายการไหน/เพราะอะไร
        // ไม่ refetch (แถวที่ลบไปแล้วยังโชว์) และ toast สำเร็จไม่มีตัวเลข ("ลบแพ็กเกจซิม  รายการ")
        // หลังมีด่านห้ามลบแพ็กเกจที่ซิมใช้อยู่ การลบบางส่วนเป็นเรื่องปกติ จึงใช้ helper เดียวกับหน้าซิม
        const result = await runBulkDelete(selectedPackages, (id) => simPackageService.deleteSimPackage(id));
        setSelectedPackages(result.failed.map((f) => f.id));
        refetch();
        if (result.failed.length === 0) {
          showAlert('สำเร็จ', `ลบแพ็กเกจซิม ${result.succeeded.length} รายการเรียบร้อยแล้ว`, 'success');
        } else {
          showAlert(
            result.succeeded.length ? 'ลบได้บางรายการ' : 'ลบไม่สำเร็จ',
            bulkDeleteFailureMessage('แพ็กเกจซิม', result),
            result.succeeded.length ? 'warning' : 'error'
          );
        }
      } finally {
        setBulkLoading(false);
      }
    }
  };

  const openModal = (mode: 'create' | 'edit' | 'view', pkg?: SimPackage) => {
    setModalMode(mode);
    setSelectedPackage(pkg || null);

    if (mode === 'create') {
      setFormData({
        code: '',
        carrier: Carrier.TRUE,
        name: '',
        speed_mbps: undefined,
        data_quota_gb: undefined,
        billing_cycle: BillingCycle.MONTHLY,
        monthly_cost: undefined,
        yearly_cost: undefined,
        is_active: true,
      });
    } else if (pkg) {
      setFormData({
        code: pkg.code,
        carrier: pkg.carrier,
        name: pkg.name,
        speed_mbps: pkg.speed_mbps,
        data_quota_gb: pkg.data_quota_gb,
        billing_cycle: pkg.billing_cycle,
        monthly_cost: pkg.monthly_cost,
        yearly_cost: pkg.yearly_cost,
        is_active: pkg.is_active,
      });
    }

    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPackage(null);
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.code?.trim()) {
      errors.code = 'กรุณาระบุรหัสแพ็กเกจ';
    }
    if (!formData.name?.trim()) {
      errors.name = 'กรุณาระบุชื่อแพ็กเกจ';
    }
    if (!formData.carrier) {
      errors.carrier = 'กรุณาเลือกค่ายมือถือ';
    }

    // Validate numeric fields - must be >= 0
    if (formData.speed_mbps !== undefined && formData.speed_mbps < 0) {
      errors.speed_mbps = 'ค่าต้องเป็นตัวเลขที่มากกว่าหรือเท่ากับ 0';
    }
    if (formData.data_quota_gb !== undefined && formData.data_quota_gb < 0) {
      errors.data_quota_gb = 'ค่าต้องเป็นตัวเลขที่มากกว่าหรือเท่ากับ 0';
    }
    if (formData.monthly_cost !== undefined && formData.monthly_cost < 0) {
      errors.monthly_cost = 'ค่าต้องเป็นตัวเลขที่มากกว่าหรือเท่ากับ 0';
    }
    if (formData.yearly_cost !== undefined && formData.yearly_cost < 0) {
      errors.yearly_cost = 'ค่าต้องเป็นตัวเลขที่มากกว่าหรือเท่ากับ 0';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;  // BUG-S795: block same-tick 2nd submit while create in-flight
    if (!validateForm()) return;

    if (modalMode === 'create') {
      isSubmittingRef.current = true;
      createMutation.mutate(formData, {
        onSettled: () => { isSubmittingRef.current = false; }
      });
    } else if (modalMode === 'edit' && selectedPackage) {
      updateMutation.mutate({ id: selectedPackage.id, data: formData, ifMatch: (selectedPackage as any).updated_at || (selectedPackage as any).created_at });
    }
  };

  const handleDelete = async (pkg: SimPackage) => {
    const confirmed = await showConfirm(
      'ยืนยันการลบ',
      `คุณต้องการลบแพ็กเกจ "${pkg.name}" ใช่หรือไม่?`,
      undefined,
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );

    if (confirmed) {
      deleteMutation.mutate(pkg.id);
    }
  };

  const handleToggle = async (pkg: SimPackage) => {
    const newStatus = pkg.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน';
    const confirmed = await showConfirm(
      'ยืนยันการเปลี่ยนสถานะ',
      `คุณต้องการ${newStatus}แพ็กเกจ "${pkg.name}" ใช่หรือไม่?`
    );

    if (confirmed) {
      toggleMutation.mutate(pkg.id);
    }
  };

  // Filter configuration
  const filterConfigs: FilterConfig[] = [
    {
      key: 'carrier',
      type: 'select',
      label: 'ค่ายมือถือ',
      value: filterCarrier,
      onChange: (value) => {
        setFilterCarrier(value);
        setCurrentPage(1);
      },
      options: [
        { value: '', label: 'ทั้งหมด' },
        ...Object.entries(CarrierLabels).map(([value, label]) => ({ value, label })),
      ],
    },
    {
      key: 'billing_cycle',
      type: 'select',
      label: 'รอบบิล',
      value: filterBillingCycle,
      onChange: (value) => {
        setFilterBillingCycle(value);
        setCurrentPage(1);
      },
      options: [
        { value: '', label: 'ทั้งหมด' },
        ...Object.entries(BillingCycleLabels).map(([value, label]) => ({ value, label })),
      ],
    },
    {
      key: 'is_active',
      type: 'select',
      label: 'สถานะ',
      value: filterStatus,
      onChange: (value) => {
        setFilterStatus(value);
        setCurrentPage(1);
      },
      options: [
        { value: '', label: 'ทั้งหมด' },
        { value: 'active', label: 'ใช้งาน' },
        { value: 'inactive', label: 'ไม่ใช้งาน' },
      ],
    },
  ];

  // Table columns
  const columns = [
    { key: 'code', title: 'รหัส', sortable: true },
    { key: 'carrier', title: 'ค่าย', sortable: true },
    { key: 'name', title: 'ชื่อแพ็กเกจ', sortable: true },
    { key: 'speed_mbps', title: 'ความเร็ว (Mbps)', sortable: true },
    { key: 'data_quota_gb', title: 'โควต้า (GB)', sortable: true },
    { key: 'billing_cycle', title: 'รอบบิล', sortable: true },
    { key: 'cost', title: 'ค่าบริการ', sortable: true },
    { key: 'is_active', title: 'สถานะ', sortable: true },
    { key: 'actions', title: 'จัดการ' },
  ];

  // Format data for table
  const tableData = packages.map((pkg) => ({
    id: pkg.id,
    code: pkg.code,
    carrier: CarrierLabels[pkg.carrier] || pkg.carrier,
    name: pkg.name,
    speed_mbps: pkg.speed_mbps ? `${pkg.speed_mbps} Mbps` : '-',
    data_quota_gb: pkg.data_quota_gb ? `${pkg.data_quota_gb} GB` : 'Unlimited',
    billing_cycle: BillingCycleLabels[pkg.billing_cycle] || pkg.billing_cycle,
    cost: pkg.billing_cycle === BillingCycle.YEARLY
      ? (pkg.yearly_cost ? `${pkg.yearly_cost.toLocaleString()} บาท/ปี` : '-')
      : (pkg.monthly_cost ? `${pkg.monthly_cost.toLocaleString()} บาท/เดือน` : '-'),
    is_active: (
      <span
        className={`px-2 py-1 rounded-full text-xs ${
          pkg.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}
      >
        {pkg.is_active ? 'ใช้งาน' : 'ไม่ใช้งาน'}
      </span>
    ),
    actions: (
      <ActionDropdown
        actions={[
          {
            label: 'ดู',
            icon: <RiEyeLine />,
            onClick: () => openModal('view', pkg),
            visible: canViewPackage,
          },
          {
            label: 'แก้ไข',
            icon: <RiEditLine />,
            onClick: () => openModal('edit', pkg),
            visible: canEditPackage,
          },
          {
            label: pkg.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน',
            icon: pkg.is_active ? <RiToggleFill /> : <RiToggleLine />,
            onClick: () => handleToggle(pkg),
            visible: canEditPackage,
          },
          {
            label: 'ลบ',
            icon: <RiDeleteBinLine />,
            onClick: () => handleDelete(pkg),
            visible: canDeletePackage,
            variant: 'danger',
          },
        ]}
      />
    ),
  }));


  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-secondary-900 flex items-center gap-2">
              <RiSimCard2Line className="text-primary-600" />
              จัดการแพ็กเกจซิม
            </h1>
            <p className="text-xs text-secondary-500 mt-1">จัดการข้อมูลแพ็กเกจซิมสำหรับการเช่า</p>
          </div>
          <ActionButtonsRow
            onAdd={() => openModal('create')}
            addLabel="เพิ่มแพ็กเกจ"
            showAdd={canCreatePackage}
            showRefresh={false}
            showExport={false}
          />
        </div>

        <ListLoadErrorBanner error={error} entityLabel="แพ็กเกจซิม" onRetry={() => refetch()} />

        {/* Search and Filters */}
        <SearchFilterCard
          searchValue={searchTerm}
          onSearchChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          searchPlaceholder="ค้นหาชื่อหรือรหัสแพ็กเกจ..."
          filters={filterConfigs}
        />

        {/* Bulk Actions */}
        {selectedPackages.length > 0 && canDeletePackage && (
          <BulkActionsBar
            selectedCount={selectedPackages.length}
            onBulkDelete={handleBulkDelete}
            loading={bulkLoading}
            onClearSelection={() => setSelectedPackages([])}
            deleteLabel="ลบรายการที่เลือก"
          />
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow">
          <Table
            columns={columns}
            data={tableData}
            loading={isLoading}
            selectable={canDeletePackage}
            selectedIds={selectedPackages}
            onSelectAll={handleSelectAll}
            onSelectRow={handleSelectPackage}
          />

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </div>

        {/* Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title={
            modalMode === 'create'
              ? 'เพิ่มแพ็กเกจซิม'
              : modalMode === 'edit'
              ? 'แก้ไขแพ็กเกจซิม'
              : 'รายละเอียดแพ็กเกจซิม'
          }
          size="lg"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ValidatedInput
                label="รหัสแพ็กเกจ"
                value={formData.code}
                onChange={(e) => {
                  setFormData({ ...formData, code: e.target.value });
                  // Clear error ใน field code เมื่อ user เปลี่ยนค่า
                  if (formErrors.code) {
                    setFormErrors({ ...formErrors, code: '' });
                  }
                }}
                error={formErrors.code}
                disabled={(modalMode as string) === 'view'}
                required
              />
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  ค่ายมือถือ <span className="text-danger-500">*</span>
                </label>
                <Select
                  aria-label="ค่ายมือถือ"
                  value={formData.carrier}
                  onChange={(e) => setFormData({ ...formData, carrier: e.target.value as Carrier })}
                  options={Object.entries(CarrierLabels).map(([value, label]) => ({ value, label }))}
                  disabled={(modalMode as string) === 'view'}
                  aria-invalid={Boolean(formErrors.carrier)}
                  aria-describedby={formErrors.carrier ? 'carrier-error' : undefined}
                />
                {formErrors.carrier && (
                  <p id="carrier-error" className="text-sm text-danger-500 mt-1">{formErrors.carrier}</p>
                )}
              </div>
            </div>

            <ValidatedInput
              label="ชื่อแพ็กเกจ"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={formErrors.name}
              disabled={(modalMode as string) === 'view'}
              required
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ValidatedInput
                label="ความเร็ว (Mbps)"
                type="number"
                min={0}
                value={formData.speed_mbps?.toString() || ''}
                onChange={(e) => {
                  setFormData({ ...formData, speed_mbps: e.target.value ? Number(e.target.value) : undefined });
                  // Clear error when user changes value
                  if (formErrors.speed_mbps) {
                    setFormErrors({ ...formErrors, speed_mbps: '' });
                  }
                }}
                error={formErrors.speed_mbps}
                disabled={(modalMode as string) === 'view'}
              />
              <ValidatedInput
                label="โควต้าข้อมูล (GB)"
                type="number"
                min={0}
                value={formData.data_quota_gb?.toString() || ''}
                onChange={(e) => {
                  setFormData({ ...formData, data_quota_gb: e.target.value ? Number(e.target.value) : undefined });
                  // Clear error when user changes value
                  if (formErrors.data_quota_gb) {
                    setFormErrors({ ...formErrors, data_quota_gb: '' });
                  }
                }}
                error={formErrors.data_quota_gb}
                disabled={(modalMode as string) === 'view'}
                placeholder="ว่างไว้ = Unlimited"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">รอบบิล</label>
                <Select
                  aria-label="รอบบิล"
                  value={formData.billing_cycle || BillingCycle.MONTHLY}
                  onChange={(e) => setFormData({ ...formData, billing_cycle: e.target.value as BillingCycle })}
                  options={Object.entries(BillingCycleLabels).map(([value, label]) => ({ value, label }))}
                  disabled={(modalMode as string) === 'view'}
                />
              </div>
              <div className="flex items-center pt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    disabled={(modalMode as string) === 'view'}
                    className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-secondary-700">เปิดใช้งาน</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ValidatedInput
                label="ค่าบริการรายเดือน (บาท)"
                type="number"
                min={0}
                value={formData.monthly_cost?.toString() || ''}
                onChange={(e) => {
                  setFormData({ ...formData, monthly_cost: e.target.value ? Number(e.target.value) : undefined });
                  // Clear error when user changes value
                  if (formErrors.monthly_cost) {
                    setFormErrors({ ...formErrors, monthly_cost: '' });
                  }
                }}
                error={formErrors.monthly_cost}
                disabled={(modalMode as string) === 'view'}
              />
              <ValidatedInput
                label="ค่าบริการรายปี (บาท)"
                type="number"
                min={0}
                value={formData.yearly_cost?.toString() || ''}
                onChange={(e) => {
                  setFormData({ ...formData, yearly_cost: e.target.value ? Number(e.target.value) : undefined });
                  // Clear error when user changes value
                  if (formErrors.yearly_cost) {
                    setFormErrors({ ...formErrors, yearly_cost: '' });
                  }
                }}
                error={formErrors.yearly_cost}
                disabled={(modalMode as string) === 'view'}
              />
            </div>

            {/* Modal Actions */}
            {modalMode !== 'view' && (
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-secondary-700 bg-secondary-100 rounded-md hover:bg-secondary-200"
                >
                  ยกเลิก
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'กำลังบันทึก...'
                    : modalMode === 'create'
                    ? 'สร้าง'
                    : 'บันทึก'}
                </button>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export default SimPackagePage;
